// src/hooks/useGenotyperRun.ts
import { useQuery } from "@tanstack/react-query";

import { fetchGenotyperRun } from "../../api/labiq/GenotyperApi";
import type { GenotyperRun } from "../../types/lab/GenotyperTypes";
import { isTerminalRunStatus } from "../../utils/genotyper";

export const GENOTYPER_RUN_QUERY_KEY = "genotyper-run";

// A run takes minutes; 30s keeps the panel current without hammering labiq.
export const GENOTYPER_RUN_POLL_INTERVAL_MS = 30_000;

export interface UseGenotyperRunOptions {
	enabled?: boolean;
	pollIntervalMs?: number;
}

export interface UseGenotyperRunResult {
	run: GenotyperRun | undefined;
	isLoading: boolean;
	isFetching: boolean;
	isError: boolean;
	error: Error | null;
	isPolling: boolean;
	refetch: () => void;
}

export const useGenotyperRun = (resultId?: number | null, options: UseGenotyperRunOptions = {}): UseGenotyperRunResult => {
	const { enabled = true, pollIntervalMs = GENOTYPER_RUN_POLL_INTERVAL_MS } = options;

	const query = useQuery<GenotyperRun, Error>({
		queryKey: [GENOTYPER_RUN_QUERY_KEY, resultId],
		queryFn: () => fetchGenotyperRun(resultId as number),
		enabled: !!resultId && enabled,
		staleTime: 0,
		refetchOnWindowFocus: false,
		refetchIntervalInBackground: false,
		// Reads the query's own state, so the interval stops the moment a terminal status lands.
		refetchInterval: (q) => (q.state.data && !isTerminalRunStatus(q.state.data.status) ? pollIntervalMs : false),
	});

	return {
		run: query.data,
		isLoading: query.isLoading,
		isFetching: query.isFetching,
		isError: query.isError,
		error: query.error,
		isPolling: !!query.data && !isTerminalRunStatus(query.data.status),
		refetch: () => {
			void query.refetch();
		},
	};
};

export default useGenotyperRun;
